import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { listAccessForUser } from '@/lib/digitalAccess';

export interface DigitalDownload {
  productId: number;
  orderId: number | null;
  source: string;
  name: string;
  fileName: string | null;
  note: string | null;
  url: string | null;
  quantity: number;
  date: string | null;
}

interface ProductRow {
  id: number;
  name: string;
  product_type: string | null;
  digital_file_url: string | null;
  digital_file_name: string | null;
  digital_delivery_note: string | null;
}

/**
 * Produits DIGITAUX auxquels un client connecté a accès.
 * L'accès provient de la table `digital_product_access` : soit accordé
 * manuellement par le marchand, soit créé automatiquement quand le marchand
 * valide le paiement de la commande du client.
 */
export function useDigitalDownloads(userId?: number | string | null) {
  const [downloads, setDownloads] = useState<DigitalDownload[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const uid = userId != null ? Number(userId) : NaN;
    if (!Number.isFinite(uid)) {
      setDownloads([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const access = await listAccessForUser(uid);
      if (access.length === 0) {
        setDownloads([]);
        return;
      }

      const productIds = Array.from(new Set(access.map((a) => a.productId)));
      const { data: products, error: prodError } = await supabase
        .from('product_items')
        .select('id, name, product_type, digital_file_url, digital_file_name, digital_delivery_note')
        .in('id', productIds);
      if (prodError) throw prodError;

      const productMap = new Map<number, ProductRow>();
      ((products as ProductRow[]) || []).forEach((p) => productMap.set(Number(p.id), p));

      const list: DigitalDownload[] = access
        .map((a) => {
          const product = productMap.get(a.productId);
          return {
            productId: a.productId,
            orderId: a.orderId,
            source: a.source,
            name: product?.name || a.productName || `Produit #${a.productId}`,
            fileName: product?.digital_file_name || null,
            note: product?.digital_delivery_note || null,
            url: product?.digital_file_url || null,
            quantity: 1,
            date: a.grantedAt || null,
          };
        })
        .filter((d) => d.name);

      setDownloads(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger vos téléchargements.');
      setDownloads([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { downloads, loading, error, reload };
}

export default useDigitalDownloads;